import type { ReactElement } from 'react'
import React from 'react'
import { Pressable, Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import ButtonIcon from '@ui-kit/components/ButtonIcon'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { Popup, type PopupBackdrop } from '@ui-kit/components/Popup'
import { body2, h4 } from '@ui-kit/core/typography'

const infoButton = tv({
  slots: {
    closeButton:
      'absolute right-0 top-0 z-10 h-10 w-10 items-center justify-center rounded-full bg-grey-100',
    content: 'relative flex flex-col gap-y-3 pb-6 pr-12',
    title: h4(),
    text: body2(),
  },
})

type InfoButtonType = 'info' | 'warning'

interface Props {
  modalTitle?: string
  modalContent: string | ReactElement
  type: InfoButtonType
  onOpen?: () => void
  backdrop?: PopupBackdrop
}

const InfoButton = (props: Props) => {
  const [isPopupOpen, setIsPopupOpen] = React.useState<boolean>(false)

  const openPopup = () => {
    setIsPopupOpen(true)
    props.onOpen?.()
  }

  const closePopup = () => {
    setIsPopupOpen(false)
  }

  const getIcon = (type: InfoButtonType) => {
    switch (type) {
      case 'info': {
        return <PhosphorIcon name="Info" size={24} />
      }
      case 'warning': {
        return <PhosphorIcon name="ExclamationMark" size={24} />
      }
      default: {
        throw new Error(`Assertion: unexpected InfoButton type ${type}`)
      }
    }
  }

  const { closeButton, content, title, text } = infoButton()

  return (
    <View>
      <ButtonIcon icon={getIcon(props.type)} onPress={openPopup} />
      <Popup visible={isPopupOpen} onRequestClose={closePopup} backdrop={props.backdrop} noClose>
        <View className={content()}>
          <Pressable className={closeButton()} onPress={closePopup} testID="PopupCloseButton">
            <PhosphorIcon name="X" size={24} />
          </Pressable>
          {props.modalTitle ? <Text className={title()}>{props.modalTitle}</Text> : null}
          {typeof props.modalContent === 'string' ? (
            <Text className={text()}>{props.modalContent}</Text>
          ) : (
            props.modalContent
          )}
        </View>
      </Popup>
    </View>
  )
}

export default InfoButton
